import {
  EditOutline24Icon,
  StopwatchOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { format } from "date-fns/format";
import { isEqual } from "date-fns/isEqual";
import { nb } from "date-fns/locale/nb";
import { setMilliseconds } from "date-fns/setMilliseconds";
import { ChangeActualTime } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/ChangeActualTime";
import {
  getColor,
  getIsBetweenStops,
  isStopCancelled,
  timeFromKeys,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/utils";
import { IconButton, Text, Tooltip } from "shared/components/Spor";
import { StopIcon } from "features/CenterContent/shared/StopIcon";
import {
  Line,
  RouteFraction,
  TrainTime,
  VehicleStopWrapper,
} from "features/CenterContent/shared/styles";
import { FC, useState } from "react";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import {
  AlternativeTransportItinerary,
  AlternativeTransportItineraryStop,
  stopActivitySchema,
  StopReference,
  VehicleStatus,
} from "shared/types/alternativeTransport";
import styled, { useTheme } from "styled-components";
import { z } from "zod";
import { differenceInMinutes } from "date-fns";

type Props = {
  id: string;
  travelDate: Date | null;
  itinerary: AlternativeTransportItinerary | null;
  vehicleStatus: VehicleStatus;
  distributionStopReferences: Array<StopReference>;
};

const InfoHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  margin: 0 10px 3px 0;
  height: 21px;
`;

const StopName = styled.p<{ color: string }>`
  font-family: "Vy Sans", sans-serif;
  font-weight: bold;
  padding: 0;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  color: ${({ color }) => color};
`;

const ItineraryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow-x: scroll;
  scrollbar-color: ${({ theme }) => theme.colorSelected} rgba(0, 0, 0, 0);
`;

export const TimeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 6px;
  height: 24px;
`;

export const InnerTimingBundle = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimeRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;

  > div {
    display: flex;
    flex-direction: column;
  }

  > *:not(:last-child) {
    margin-right: 12px;
  }
`;

export const EditButtonWrapper = styled.div`
  display: none;
  margin-left: 9px;
  justify-content: center;
  align-items: center;

  ${TimeWrapper}:hover & {
    display: inline-flex;
  }
`;

const stops = (itinerary: AlternativeTransportItinerary | null) => {
  if (itinerary === null) {
    return [];
  }
  if (itinerary.returnStops !== null && itinerary.returnStops.length > 0) {
    return itinerary.stops.concat(itinerary.returnStops.slice(1));
  }
  return itinerary.stops;
};

type TimesProps = {
  actualOrEstimatedTime: Date | null;
  scheduledTime: Date | null;
  isCancelled: boolean;
};

export const BusTimes: FC<TimesProps> = ({
  actualOrEstimatedTime,
  scheduledTime,
  isCancelled,
}) => {
  const isEqualToTheMinute =
    actualOrEstimatedTime &&
    scheduledTime &&
    isEqual(
      setMilliseconds(actualOrEstimatedTime, 0),
      setMilliseconds(scheduledTime, 0),
    );

  const hasTimeDeviation = !!actualOrEstimatedTime && !isEqualToTheMinute;

  return (
    <TimeRow>
      <div>
        {scheduledTime && (
          <TrainTime
            $hasActualTime
            $isCancelled={hasTimeDeviation || isCancelled}
          >
            {format(scheduledTime, "HH:mm", { locale: nb })}
          </TrainTime>
        )}
        {hasTimeDeviation && (
          <TrainTime $hasActualTime $isCancelled={isCancelled}>
            {format(actualOrEstimatedTime, "HH:mm", { locale: nb })}
          </TrainTime>
        )}
      </div>
      {hasTimeDeviation && scheduledTime && (
        <TrainTime
          $hasActualTime
          $isCancelled={isCancelled}
          style={{
            borderLeft: "1px solid",
            paddingLeft: "6px",
            opacity: 0.5,
          }}
        >
          {`${
            differenceInMinutes(actualOrEstimatedTime, scheduledTime) > 0
              ? "+"
              : "-"
          }${differenceInMinutes(actualOrEstimatedTime, scheduledTime)}`}
        </TrainTime>
      )}
    </TimeRow>
  );
};

export const ItineraryDetails: FC<Props> = ({
  id,
  travelDate,
  itinerary,
  vehicleStatus,
  distributionStopReferences,
}) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [selectedStop, setSelectedStop] =
    useState<AlternativeTransportItineraryStop | null>(null);
  const allStops = stops(itinerary);
  if (allStops.length === 0) {
    return <NoDataMessage reason="Fant ingen rute for dette kjøretøyet" />;
  }

  const closeModal = () => {
    setShowModal(false);
    setSelectedStop(null);
  };

  const openModalForStop = (stop: AlternativeTransportItineraryStop) => {
    setSelectedStop(stop);
    setShowModal(true);
  };

  const getActivity = (activity: z.infer<typeof stopActivitySchema>) => {
    switch (activity) {
      case "BOARDING":
        return "Kun påstigning";
      case "DISEMBARKING":
        return "Kun avstigning";

      case "BOARDING_AND_DISEMBARKING":
      case "NONE":
        return null;
    }
  };

  return (
    <>
      <ItineraryWrapper>
        {allStops.map((stop, index, array) => {
          const arrivalTimes = {
            actualOrEstimatedTime: timeFromKeys(stop, [
              "actualArrivalTime",
              "estimatedArrivalTime",
            ]),
            scheduledTime: timeFromKeys(stop, ["scheduledArrivalTime"]),
          };

          const departureTimes = {
            actualOrEstimatedTime: timeFromKeys(stop, [
              "actualDepartureTime",
              "estimatedDepartureTime",
            ]),
            scheduledTime: timeFromKeys(stop, ["scheduledDepartureTime"]),
          };

          const { lineColor, stopColor, textColor } = getColor(stop, theme);
          const stopIsCancelled = isStopCancelled(stop, vehicleStatus);
          const activity = getActivity(stop.activity);

          const isBetween = getIsBetweenStops(allStops, stop, index);
          const isMissingData = !arrivalTimes.actualOrEstimatedTime;
          return (
            <VehicleStopWrapper key={stop.name}>
              <RouteFraction className="route_fraction">
                <StopIcon
                  justPassingThrough={activity === "Kun påstigning"}
                  isCancelled={stopIsCancelled}
                  color={stopColor}
                />
                {!(index === array.length - 1) && (
                  <Line
                    $isInBetweenStops={isBetween}
                    $isMissingData={false}
                    $color={lineColor}
                  />
                )}
              </RouteFraction>
              <InfoHeader>
                <StopName color={textColor}>{stop.name}</StopName>
                {isMissingData && (
                  <Tooltip
                    hasArrow
                    placement="right"
                    label="Det finnes ikke passeringsmeldinger for dette stoppet"
                  >
                    <span style={{ cursor: "pointer" }}>
                      <StopwatchOutline18Icon
                        color={theme.colorTextSecondary}
                      />
                    </span>
                  </Tooltip>
                )}
              </InfoHeader>
              {activity && <Text variant="secondary">{activity}</Text>}

              <TimeWrapper>
                <InnerTimingBundle>
                  <BusTimes
                    {...arrivalTimes}
                    isCancelled={stop.isArrivalCancelled}
                  />
                  <BusTimes {...departureTimes} isCancelled={stopIsCancelled} />
                </InnerTimingBundle>
                <EditButtonWrapper>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    icon={<EditOutline24Icon />}
                    onClick={() => openModalForStop(stop)}
                    aria-label="Endre tidspunkt for stoppested"
                  />
                </EditButtonWrapper>
              </TimeWrapper>
            </VehicleStopWrapper>
          );
        })}
      </ItineraryWrapper>
      {travelDate && (
        <ChangeActualTime
          id={id}
          travelDate={travelDate}
          distributionStopReferences={distributionStopReferences}
          showModal={showModal}
          stop={selectedStop}
          closeModal={closeModal}
        />
      )}
    </>
  );
};
